import React from "react";
import "../../styles/HowToRegisterAsAttendee.css";

// Import images
import homeSearch from "../../ui-assets/help/how-to-register-as-attendee/home-search.png";
import upcomingEvents from "../../ui-assets/help/how-to-register-as-attendee/upcoming-events.png";
import eventPage from "../../ui-assets/help/how-to-register-as-attendee/event-page.png";
import registrationForm from "../../ui-assets/help/how-to-register-as-attendee/registration-form.png";
// NOTE: this still points at registration-form.png (same as step 04).
// Swap in a dedicated "submit" screenshot when you have one.
import submitRegistration from "../../ui-assets/help/how-to-register-as-attendee/registration-form.png";

const steps = [
  {
    number: "01",
    title: "Open EventFork",
    text: (
      <>
        Open <strong>EventFork</strong> and go to the home page. You can browse
        the available events and use the search bar to find the event you want
        to attend.
      </>
    ),
    image: homeSearch,
    alt: "EventFork home page with the event search bar",
  },
  {
    number: "02",
    title: "Search and find your event",
    text: (
      <>
        Type the event name in the <strong>Search events</strong> field and
        select the required event from the available results. You can also
        browse the Upcoming Events section and find the event there.
      </>
    ),
    image: upcomingEvents,
    alt: "EventFork upcoming events section",
  },
  {
    number: "03",
    title: "Choose how to register",
    text: (
      <>
        If the event card shows <strong>Register Event</strong>, you can click
        it directly. Otherwise, open the event page and click{" "}
        <strong>Register Now</strong>.
      </>
    ),
    image: eventPage,
    alt: "EventFork event page with the Register Now button",
  },
  {
    number: "04",
    title: "Fill in your details",
    text: (
      <>
        Complete the registration form with the required information, such as
        your full name, email address, phone number, gender, and any other
        fields shown for that event.
      </>
    ),
    image: registrationForm,
    alt: "EventFork attendee registration form",
  },
  {
    number: "05",
    title: "Accept the terms and submit",
    text: (
      <>
        Review the information you entered, agree to the{" "}
        <strong>Terms of Service</strong> and{" "}
        <strong>Privacy Policy</strong>, then click{" "}
        <strong>Register Now</strong> to submit your registration.
      </>
    ),
    image: submitRegistration,
    alt: "EventFork registration form ready to be submitted",
  },
];

/**
 * Props (all optional):
 *  - helpUrl → link back to the Help Center. Default: "/help"
 *  - homeUrl → breadcrumb "Home" link. Default: "/"
 */
function HowToRegisterAsAttendee({ helpUrl = "/help", homeUrl = "/" }) {
  return (
    <main className="attendee-help-page">
      {/* ── Compact header ── */}
      <header className="attendee-help-hero">
        <div className="attendee-help-hero-inner">
          <div>
            <nav className="attendee-help-breadcrumb" aria-label="Breadcrumb">
              <a href={homeUrl}>Home</a>
              <span aria-hidden="true">/</span>
              <a href={helpUrl}>Help Center</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Register as attendee</span>
            </nav>
            <h1>How to Register as an Attendee</h1>
            <p>Find an event on EventFork and complete your registration.</p>
          </div>
          <span className="attendee-help-chip">
            <i className="fas fa-route" aria-hidden="true" />
            {steps.length} steps
          </span>
        </div>
      </header>

      <section className="attendee-help-content">
        {/* ── Two ways to start ── */}
        <p className="attendee-help-note">
          <i className="fas fa-info-circle" aria-hidden="true" />
          <span>
            Two ways to start: click <strong>Register Event</strong> on an
            event card, or open the event page and click{" "}
            <strong>Register Now</strong>.
          </span>
        </p>

        {/* ── Snake path ── */}
        <ol className="attendee-help-snake">
          {steps.map((step, i) => {
            const reverse = i % 2 === 1;
            const last = i === steps.length - 1;
            return (
              <li
                key={step.number}
                className={`attendee-help-step${reverse ? " is-reverse" : ""}`}
              >
                {/* rail: node + the line that carries on to the next bend */}
                <div className="attendee-help-rail" aria-hidden="true">
                  <span className="attendee-help-node">{step.number}</span>
                  <span className="attendee-help-line" />
                  {last && (
                    <span className="attendee-help-node attendee-help-node--end">
                      <i className="fas fa-check" />
                    </span>
                  )}
                </div>

                <article className="attendee-help-card">
                  <div className="attendee-help-copy">
                    <h2>
                      <span className="attendee-help-sr">
                        Step {step.number}:{" "}
                      </span>
                      {step.title}
                    </h2>
                    <p>{step.text}</p>
                  </div>

                  <figure className="attendee-help-image-wrap">
                    <img src={step.image} alt={step.alt} loading="lazy" />
                    <figcaption>{step.title}</figcaption>
                  </figure>
                </article>
              </li>
            );
          })}
        </ol>

        {/* ── Done ── */}
        <section className="attendee-help-summary">
          <div className="attendee-help-summary-number">
            <i className="fas fa-check" aria-hidden="true" />
          </div>
          <div>
            <h2>Your registration is ready</h2>
            <p>
              Once you submit the form, your attendee registration request has
              been sent for the selected event.
            </p>
          </div>
          <a href={helpUrl}>Back to Help Center</a>
        </section>
      </section>
    </main>
  );
}

export default HowToRegisterAsAttendee;